import type { ShiftEditorDraftEndpoint } from '@der-maschinfuehrer/contracts';
import { useEffect, useId, useRef, useState } from 'react';

import { catalogDocumentToLocationSnapshot } from '../../catalog/catalog-client';
import type { CatalogClient } from '../../catalog/catalog-client';
import { ACTIVE_CATALOG_VERSION, type DraftEndpointUpdateValue } from '../../domain/alpha-shift';
import { DraftEndpointField } from './DraftEndpointField';

interface PendingFocus {
  readonly index: number | null;
}

export function ViaPointsEditor({
  client,
  disabled,
  onAdd,
  onChange,
  onDuplicate,
  onMove,
  onRemove,
  points,
}: {
  readonly client: CatalogClient;
  readonly disabled: boolean;
  readonly onAdd: (value?: DraftEndpointUpdateValue) => void;
  readonly onChange: (index: number, value: DraftEndpointUpdateValue) => void;
  readonly onDuplicate: (index: number) => void;
  readonly onMove: (index: number, direction: 'down' | 'up') => void;
  readonly onRemove: (index: number) => void;
  readonly points: readonly ShiftEditorDraftEndpoint[];
}) {
  const headingId = useId();
  const addButton = useRef<HTMLButtonElement>(null);
  const pointInputs = useRef(new Map<number, HTMLInputElement>());
  const pendingFocus = useRef<PendingFocus | null>(null);
  const [repeatPointIndex, setRepeatPointIndex] = useState('');
  const repeatedPoint =
    repeatPointIndex === '' ? undefined : points[Number.parseInt(repeatPointIndex, 10)];

  useEffect(() => {
    const pending = pendingFocus.current;
    if (pending === null) return;
    pendingFocus.current = null;
    if (pending.index === null) {
      addButton.current?.focus();
    } else {
      pointInputs.current.get(pending.index)?.focus();
    }
  }, [points]);

  const addPoint = () => {
    pendingFocus.current = { index: points.length };
    onAdd();
  };

  const removePoint = (index: number) => {
    setRepeatPointIndex('');
    pendingFocus.current = {
      index: points.length === 1 ? null : Math.min(index, points.length - 2),
    };
    onRemove(index);
  };

  const movePoint = (index: number, direction: 'down' | 'up') => {
    setRepeatPointIndex('');
    pendingFocus.current = { index: direction === 'up' ? index - 1 : index + 1 };
    onMove(index, direction);
  };

  return (
    <section className="via-points-editor" aria-labelledby={headingId}>
      <h3 id={headingId}>Body Přes</h3>
      <p className="field-description">
        Přidej skutečně projetá místa v přesném pořadí. Stejné místo může být v trase i vícekrát.
        Pořadí lze vždy změnit tlačítky, není nutné přetahování.
      </p>

      {points.length === 0 ? <p>Trasa zatím nemá žádný bod Přes.</p> : null}
      {points.length > 0 ? (
        <ol className="via-point-list">
          {points.map((point, index) => (
            <li className="via-point-item" key={`${point.location_id}:${String(index)}`}>
              <DraftEndpointField
                ref={(node) => {
                  if (node === null) {
                    pointInputs.current.delete(index);
                  } else {
                    pointInputs.current.set(index, node);
                  }
                }}
                client={client}
                disabled={disabled}
                endpoint={point}
                label={`Přes ${String(index + 1)}`}
                name={`via-${String(index)}`}
                required
                onManualChange={(value) => onChange(index, value)}
                onSelect={(document) => {
                  const snapshot = catalogDocumentToLocationSnapshot(document);
                  onChange(index, {
                    name: snapshot.display_name,
                    country_code: document.country,
                    key_identifier: snapshot.key_identifiers[0] ?? null,
                    carried_location_snapshot: snapshot,
                    catalog_document_id: document.id,
                    catalog_version: ACTIVE_CATALOG_VERSION,
                  });
                }}
              />
              <div className="inline-actions via-point-actions">
                <button
                  type="button"
                  className="secondary-action touch-target"
                  disabled={disabled || index === 0}
                  onClick={() => movePoint(index, 'up')}
                >
                  Posunout Přes {index + 1} nahoru
                </button>
                <button
                  type="button"
                  className="secondary-action touch-target"
                  disabled={disabled || index === points.length - 1}
                  onClick={() => movePoint(index, 'down')}
                >
                  Posunout Přes {index + 1} dolů
                </button>
                <button
                  type="button"
                  className="danger-action touch-target"
                  disabled={disabled}
                  onClick={() => removePoint(index)}
                >
                  Odebrat Přes {index + 1}
                </button>
              </div>
            </li>
          ))}
        </ol>
      ) : null}

      {points.length > 0 ? (
        <div className="via-repeat-controls">
          <label className="form-field">
            <span>Zopakovat projeté místo</span>
            <select
              className="text-input touch-target"
              disabled={disabled}
              value={repeatPointIndex}
              onChange={(event) => setRepeatPointIndex(event.currentTarget.value)}
            >
              <option value="">Vyber bod Přes</option>
              {points.map((point, index) => (
                <option key={`${point.location_id}:${String(index)}`} value={String(index)}>
                  {String(index + 1)}. {point.name || 'nevyplněno'}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={
              disabled ||
              repeatedPoint === undefined ||
              repeatedPoint.name.trim() === '' ||
              points.length >= 128
            }
            onClick={() => {
              if (repeatedPoint === undefined || repeatPointIndex === '') return;
              pendingFocus.current = { index: points.length };
              onDuplicate(Number.parseInt(repeatPointIndex, 10));
            }}
          >
            Přidat znovu vybraný bod Přes
          </button>
        </div>
      ) : null}

      <button
        ref={addButton}
        type="button"
        className="secondary-action touch-target"
        disabled={disabled || points.length >= 128}
        onClick={() => addPoint()}
      >
        {points.length >= 128 ? 'Limit 128 bodů Přes dosažen' : 'Přidat bod Přes'}
      </button>
    </section>
  );
}
